/* eslint-disable no-unsafe-optional-chaining */
import React, { useContext, createContext, useCallback, useEffect, useRef, useState } from 'react'
import { useFeatureFlag } from '@fs/zion-flags'
import { useUser } from '@fs/zion-user'
import checkForEditSpam from './checkForEditSpam'
import checkForFrequencySpam from './checkForFrequencySpam'
import checkForSameTypeSpam from './checkForSameTypeSpam'

export const FlaggedUserContext = createContext(null)

export default function FlaggedUserContextProvider({ children }) {
  const editCache = useRef([])
  const frequencyCache = useRef([])
  const sameTypeCache = useRef([])
  const consecutiveSpamEpisodes = useRef({ type: '', count: 0 })
  const { config, isOn } = useFeatureFlag('rip_sharedImageViewer_flaggedUserDetection_')
  const { cisId } = useUser()

  // Get the config values from the feature flag
  const interval = +config?.intervalInSeconds || 6
  const spamCount = +config?.spamCountWithinInterval || 10
  const editThreshold = +config?.editCumulativeThreshold || 3
  const garbageThreshold = +config?.garbageDetectionThreshold || 0.51
  const matchThreshold = +config?.matchCumulativeThreshold || 20
  const notPersonThreshold = +config?.notPersonCumulativeThreshold || 6

  const [frequencySpamData, setFrequencySpamData] = useState({
    isFrequencySpamming: false,
    spamStartTime: null,
    spamStartIndex: null,
  })

  // Increment the number of consecutive spam episodes the user has done. (An episode is a group of spam tasks that are all the same type.)
  const incrementConsecutiveSpam = (incomingSpamType) => {
    const { type, count } = consecutiveSpamEpisodes.current

    if (incomingSpamType === type) consecutiveSpamEpisodes.current.count = count + 1
    else consecutiveSpamEpisodes.current = { type: incomingSpamType, count: 1 }
  }

  const checkForFlaggedBehavior = useCallback(
    (mostRecentTask) => {
      if (!isOn || !mostRecentTask) return

      // Add the most recent task to the caches
      frequencyCache.current = [...(frequencyCache.current || []), mostRecentTask]
      sameTypeCache.current = [...(sameTypeCache.current || []), mostRecentTask]

      try {
        const type = mostRecentTask?.actionType

        // If the user has stopped spammy behavior by switching buttons, reset the consecutive spam episode data
        if (consecutiveSpamEpisodes.current.type !== type) consecutiveSpamEpisodes.current = { type: '', count: 0 }

        // Check for edit spam
        if (type === 'edit') {
          editCache.current = [...editCache.current, mostRecentTask]

          if (editCache.current?.length < editThreshold) return
          checkForEditSpam({
            cisId,
            consecutiveSpamEpisodes,
            taskCache: editCache,
            editThreshold,
            garbageThreshold,
            incrementConsecutiveSpam,
          })

          // Check for frequency & same-type spamming on match and notAPerson
        } else {
          const threshold = type === 'match' ? matchThreshold : notPersonThreshold

          checkForFrequencySpam({
            cisId,
            count: spamCount,
            frequencySpamData,
            interval,
            taskCache: frequencyCache,
            setFrequencySpamData,
            type,
          })

          checkForSameTypeSpam({
            cisId,
            consecutiveSpamEpisodes,
            incrementConsecutiveSpam,
            taskCache: sameTypeCache,
            threshold,
            type,
          })

          editCache.current = []
        }
      } catch (error) {
        console.error('Error occurred in checkForFlaggedBehavior:', error)
      }
    },
    [
      cisId,
      editThreshold,
      frequencySpamData,
      garbageThreshold,
      interval,
      isOn,
      matchThreshold,
      notPersonThreshold,
      spamCount,
    ]
  )

  // Sets a timer to regularly check for frequency spam.
  // We need this in order to detect when the spamming has stopped in certain use cases, such as when the user
  // decides that they're tired of spamming and goes to close the browser without pressing any more buttons.
  useEffect(() => {
    function spamCheck() {
      if (!isOn || !frequencyCache.current.length) return

      const mostRecentTask = frequencyCache.current?.slice(-1)?.[0]
      const type = mostRecentTask?.actionType

      checkForFrequencySpam({
        cisId,
        count: spamCount,
        frequencySpamData,
        interval,
        taskCache: frequencyCache,
        setFrequencySpamData,
        type,
      })
    }

    const timer = setInterval(spamCheck, 1000)

    return () => clearInterval(timer)
  }, [cisId, frequencySpamData, interval, isOn, matchThreshold, notPersonThreshold, spamCount])

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  return <FlaggedUserContext.Provider value={{ checkForFlaggedBehavior }}>{children}</FlaggedUserContext.Provider>
}

export const useFlaggedUser = () => {
  const context = useContext(FlaggedUserContext)
  if (!context) {
    throw new Error(
      'FlaggedUserContext must be used within a FlaggedUserContextProvider. If writing a test, use <StateProvider> from testUtils.js'
    )
  }
  return context
}
